import React from "react";
import { motion } from "framer-motion";
import { Coins, Server } from "lucide-react";

const AICreditConsumptionSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1100px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-7"
        >
          <h2 className="text-3xl lg:text-[40px] font-bold text-slate-900 tracking-tight leading-tight">
            How does{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              AI credit consumption
            </span>{" "}
            work?
          </h2>
        </motion.div>

        {/* Two paragraph cards */}
        <div className="w-full grid grid-cols-2 gap-5">
          {/* Card 1 — Cost of traditional software vs AI */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_16px_44px_-22px_rgba(15,23,42,0.22)] flex flex-col"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200">
                <Server className="h-5 w-5" />
              </div>
              <h3 className="text-[18px] font-bold text-slate-900 leading-tight">
                Cost of traditional software vs. cost of running AI
              </h3>
            </div>

            <div className="space-y-3.5 text-[13.5px] leading-relaxed text-slate-700">
              <p>
                Traditional software runs on <strong className="text-slate-900">normal CPU servers</strong>.
                If a practice uses our scheduling tool <strong className="text-slate-900">10× more</strong>{" "}
                in a given month, the cost to run it stays <strong className="text-slate-900">the same</strong>.
                That is why software is almost always sold as a flat monthly fee.
              </p>
              <p>
                AI is different. AI runs on <strong className="text-slate-900">GPUs and TPUs</strong> —
                specialized high-performance chips designed for the math behind large language models.
                Every time an AI agent does a task, it uses real GPU time.
              </p>
              <p>
                The result: if our AI agents do <strong className="text-slate-900">10× more work</strong> in
                a month — more calls, more verifications, more claims — it{" "}
                <strong className="text-slate-900">costs 10× more</strong> to run.
                That is true for us, for OpenAI, for Anthropic, for Google — for the entire AI industry.
              </p>
            </div>
          </motion.div>

          {/* Card 2 — What are AI credits? */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="rounded-2xl border border-blue-200 ring-1 ring-blue-100 bg-white p-7 shadow-[0_24px_60px_-22px_rgba(59,130,246,0.4)] flex flex-col"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <Coins className="h-5 w-5" />
              </div>
              <h3 className="text-[18px] font-bold text-slate-900 leading-tight">
                What are AI credits?
              </h3>
            </div>

            <div className="space-y-3.5 text-[13.5px] leading-relaxed text-slate-700">
              <p>
                Across the AI industry, every model is metered in{" "}
                <strong className="text-slate-900">tokens</strong> — roughly a unit of language the
                model has to read or write. The more tokens a job requires, the more it costs
                whoever is running it.
              </p>
              <p>
                Our <strong className="text-slate-900">AI credits work the same way</strong>. A
                credit represents the work an AI agent has to do to finish a task — reading
                context, thinking through a decision, producing a response. Tasks that need more
                reasoning, longer documents, or more back-and-forth simply consume more credits.
              </p>
              <p>
                For example: an AI agent that has to process a{" "}
                <strong className="text-slate-900">100,000-word</strong> insurance breakdown will
                consume roughly <strong className="text-slate-900">twice the credits</strong> of
                one that only has to process <strong className="text-slate-900">50,000 words</strong>.
                It's the cleanest way to keep pricing honest — light users pay less, heavy users
                pay more, and there's never a surprise.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AICreditConsumptionSlide;
